import { useMemo } from 'react'
import DatePicker from 'react-datepicker'
import { ColDef } from 'ag-grid-community'
import { InputBase, MenuItem, Typography, styled } from '@mui/material'
import { intl } from '../../../../../i18n'
import { Button } from '../../../../components/buttons'
import { colorPalette } from '../../../../style/colorPalette'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import {
  SprintReportGroupKey,
  getSprintReportGroupKeyLabel,
} from '../../../../../lib/functions/sprintReport'
import { AggregateField } from '../../../../../domain/entity/WbsItemEntity'
import TeamSelect from '../../../../components/editors/select/TeamSelect'
import Select from '../../../../components/editors/select/Select'
import SearchButton from '../../../../components/buttons/SearchButton'
import { TimeGrainToggleGroup } from '../../../../components/editors/toggle/TimeGrainToggleGroup'
import { DayOfWeekToggleGroup } from '../../../../components/editors/toggle/DayOfWeekToggleGroup'
import {
  AggregateFieldToggleGroup,
  AggregateTargetToggleGroup,
  WorkloadUnitToggleGroup,
} from '../../../../components/toggleGroups'
import FilterButton from '../../../../containers/BulkSheetView/components/header/FilterButton'
import ColumnSettingButton from '../../../../containers/BulkSheetView/components/header/ColumnSettingButton'
import FileExportButton from '../../../../containers/BulkSheetView/components/header/FileExportButton'
import DisplayFormatButton from '../../../../containers/BulkSheetView/components/header/DisplayFormatButton'
import ReloadButton from '../../../../containers/BulkSheetView/components/header/ReloadButton'
import { DayOfWeek, TimeGrain } from '../../../../../lib/functions/report'
import {
  SprintReportSearchConditionApi,
  SprintReportDateTerm,
} from '../../hooks/searchCondition'
import { SprintReportPageStateApi } from '../../hooks/pageState'
import { SprintOption } from '../../hooks/sprintOptions'
import { SprintSelect } from './SprintSelect'

type Props = {
  projectUuid: string
  searchConditionApi: SprintReportSearchConditionApi
  pageStateApi: SprintReportPageStateApi
  sprintOptions: SprintOption[]
  onClickSearch: () => void
  onClickSavedSearchCondition: () => void
  filteredColumns: ColDef[]
  onDeleteFilteredColumn: (column: ColDef) => void
  resetFilters: () => void
  onClickColumnSettingButton: (e: React.MouseEvent) => void
  columnSettingOpen: boolean
  onClickExport: () => void
  rowHeight: number
  onChangeRowHeight: (value: number) => void
}

export const Header = ({
  projectUuid,
  searchConditionApi,
  pageStateApi,
  sprintOptions,
  onClickSearch,
  onClickSavedSearchCondition,
  filteredColumns,
  onDeleteFilteredColumn,
  resetFilters,
  onClickColumnSettingButton,
  columnSettingOpen,
  onClickExport,
  rowHeight,
  onChangeRowHeight,
}: Props) => {
  const { searchCondition } = searchConditionApi

  const updateDateTerm = (patch: Partial<SprintReportDateTerm>) => {
    searchConditionApi.setDateTerm({ ...searchCondition.dateTerm, ...patch })
  }

  const datePlaceholderId = (boundary: 'start' | 'end') =>
    searchCondition.groupKey === SprintReportGroupKey.SCHEDULED_START_DATE
      ? `sprintReport.scheduledStartDate.${boundary}`
      : `sprintReport.startDate.${boundary}`

  // チーム未選択時は全スプリント、チーム選択時はそのチームのスプリントだけに絞る (カンバン同様)。
  const filteredSprintOptions = useMemo(() => {
    if (!searchCondition.teamUuid) return sprintOptions
    return sprintOptions.filter(o => o.teamUuid === searchCondition.teamUuid)
  }, [sprintOptions, searchCondition.teamUuid])

  // GroupKey で SPRINT を選んでいる場合は、列軸がスプリント単位に切り替わるので
  // 「週初め」など date 列モード前提の表示オプションは意味を失う → 非活性にする。
  const sprintColumnMode =
    searchCondition.groupKey === SprintReportGroupKey.SPRINT

  return (
    <Root>
      <ConditionRow>
        <TeamSelectBox>
          <TeamSelect
            projectUuid={projectUuid}
            teamUuid={searchCondition.teamUuid}
            onTeamChanged={searchConditionApi.setTeamUuid}
          />
        </TeamSelectBox>
        <GroupKeyBox>
          <StyledSelect
            fullWidth={true}
            value={searchCondition.groupKey}
            MenuItem={StyledMenuItem}
            onChange={event =>
              searchConditionApi.setGroupKey(
                event.target.value as SprintReportGroupKey
              )
            }
            disableUnderline={true}
            options={[
              SprintReportGroupKey.SCHEDULED_START_DATE,
              SprintReportGroupKey.SCHEDULED_END_DATE,
              SprintReportGroupKey.SPRINT,
            ]}
            getOptionLabel={(o: SprintReportGroupKey) =>
              getSprintReportGroupKeyLabel(o)
            }
            getOptionValue={(o: SprintReportGroupKey) => o}
          />
        </GroupKeyBox>
        {sprintColumnMode ? (
          <>
            <SprintSelect
              value={searchCondition.startSprintUuid}
              options={filteredSprintOptions}
              onChange={searchConditionApi.setStartSprintUuid}
              placeholder={intl.formatMessage({
                id: 'sprintReport.startSprint',
              })}
            />
            <TermLabel>〜</TermLabel>
            <SprintSelect
              value={searchCondition.endSprintUuid}
              options={filteredSprintOptions}
              onChange={searchConditionApi.setEndSprintUuid}
              placeholder={intl.formatMessage({ id: 'sprintReport.endSprint' })}
            />
          </>
        ) : (
          <>
            <DateBox>
              <DatePicker
                selected={
                  dateV2Service.fromNumber(
                    searchCondition.dateTerm.startDate
                  ) ?? null
                }
                dateFormat="yyyy/MM/dd"
                onChange={date =>
                  updateDateTerm({
                    startDate: date ? date.getTime() : undefined,
                  })
                }
                placeholderText={intl.formatMessage({
                  id: datePlaceholderId('start'),
                })}
                customInput={<InputBase />}
                popperPlacement="bottom-start"
                // ConditionRow が overflow:auto で水平スクロール許可している影響で、
                // インライン配置の popper は上下方向にもクリップされる。portal で body 直下に出して回避する。
                popperProps={{ strategy: 'fixed' }}
                fixedHeight={true}
              />
            </DateBox>
            <TermLabel>〜</TermLabel>
            <DateBox>
              <DatePicker
                selected={
                  dateV2Service.fromNumber(searchCondition.dateTerm.endDate) ??
                  null
                }
                dateFormat="yyyy/MM/dd"
                onChange={date =>
                  updateDateTerm({
                    endDate: date ? date.getTime() : undefined,
                  })
                }
                placeholderText={intl.formatMessage({
                  id: datePlaceholderId('end'),
                })}
                customInput={<InputBase />}
                popperPlacement="bottom-start"
                // ConditionRow が overflow:auto で水平スクロール許可している影響で、
                // インライン配置の popper は上下方向にもクリップされる。portal で body 直下に出して回避する。
                popperProps={{ strategy: 'fixed' }}
                fixedHeight={true}
              />
            </DateBox>
          </>
        )}
        <SearchButton onClick={onClickSearch} />
        <SavedSearchConditionButton
          onClick={onClickSavedSearchCondition}
          colorPattern="monotone"
          variant="outlined"
        >
          <SavedSearchConditionButtonLabel>
            {intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.saved.condition',
            })}
          </SavedSearchConditionButtonLabel>
        </SavedSearchConditionButton>
      </ConditionRow>
      <ToolbarRow>
        <ReloadButton onReload={onClickSearch} />
        <AggregateTargetToggleGroup
          value={pageStateApi.aggregateTarget}
          onChange={pageStateApi.setAggregateTarget}
        />
        <AggregateFieldToggleGroup
          value={pageStateApi.aggregateField}
          onChange={pageStateApi.setAggregateField}
        />
        <WorkloadUnitToggleGroup
          value={pageStateApi.workloadUnit}
          refresh={pageStateApi.setWorkloadUnit}
          disabled={
            pageStateApi.aggregateField === AggregateField.WBS_ITEM_COUNT
          }
        />
        <TimeGrainToggleGroup
          value={searchCondition.timeGrain}
          onChange={value =>
            searchConditionApi.setTimeGrain(
              value as typeof searchCondition.timeGrain
            )
          }
          disabled={sprintColumnMode}
        />
        <StartDayOfWeekLabel>
          {intl.formatMessage({ id: 'startDayOfWeek' })}
        </StartDayOfWeekLabel>
        <DayOfWeekToggleGroup
          value={searchCondition.startDayOfWeek}
          onChange={value =>
            searchConditionApi.setStartDayOfWeek(value as DayOfWeek)
          }
          excludeHoliday={true}
          disabled={
            sprintColumnMode ||
            ![TimeGrain.WEEK, TimeGrain.TWO_WEEKS].includes(
              searchCondition.timeGrain
            )
          }
        />
        <FilterButton
          filteredColumns={filteredColumns}
          onDeleteFilteredColumn={onDeleteFilteredColumn}
          resetFilters={resetFilters}
        />
        <ColumnSettingButton
          onClick={onClickColumnSettingButton}
          open={columnSettingOpen}
        />
        <DisplayFormatButton
          height={rowHeight}
          onChangeHeight={onChangeRowHeight}
        />
        <FileExportButton onClickExport={onClickExport} />
      </ToolbarRow>
    </Root>
  )
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  // 1 段目 (ConditionRow) の薄背景を画面端まで自然に伸ばすため左右 padding は持たせない。
  // 縦方向のみヘッダー全体を BulkSheetView の上に少し空けて配置する。
  padding: '10px 0',
})

// 1 段 / 2 段で共有する flex レイアウト。
// - flexWrap: nowrap + overflowX: auto … 各行は 1 行表示固定で、横幅不足時は横スクロールで逃がす。
// - 子要素の flexShrink: 0 … Autocomplete / Select / DatePicker など固有幅を維持させ、潰れて表示が崩れないようにする。
const rowBase = {
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'center',
  gap: '6px',
  flexWrap: 'nowrap' as const,
  overflowX: 'auto' as const,
  '& > *': {
    flexShrink: 0,
  },
}

// 1 段目 (検索条件) は ProductBacklog の TeamSelector と同じ半透明グレー背景で
// 「入力エリア」を示す (#D8DDE5 = monotone[2] に 4D = 30% 透過)。
// 内側 padding 10px で文字や入力欄が背景縁に張り付かないようにする。
const ConditionRow = styled('div')({
  ...rowBase,
  padding: '10px',
  backgroundColor: `${colorPalette.monotone[2]}4D`,
})

// 2 段目 (表示・操作) は ProductBacklog のツールバーに合わせて背景なしのフラット。
// 1 段目の内側 padding と揃え、かつ下の BulkSheetView (padding: '0 8px 8px') からも
// 大きく外れない位置になるよう左右 10px 入れる。
const ToolbarRow = styled('div')({
  ...rowBase,
  padding: '0 10px',
})

// 曜日トグルの直前に置く薄字ラベル。何の項目を選んでいるかを示す。
const StartDayOfWeekLabel = styled('span')({
  fontSize: '11px',
  color: colorPalette.monotone[5],
  whiteSpace: 'nowrap',
})

// ConditionRow の半透明グレー背景に同化しないよう、入力欄は白で塗りつぶす。
// TeamSelectBox は内部の MUI Autocomplete (OutlinedInput) に対してのみ白背景を適用する。
const GroupKeyBox = styled('div')({
  border: `solid 1px ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '0 5px',
  backgroundColor: colorPalette.monotone[0],
})

// DateBox は react-datepicker のクリック検知 (input の onClick で popper を開く) を壊さないよう、
// flex 化はせず元の通常フローのままにする。
const DateBox = styled('div')({
  position: 'relative',
  border: `solid 1px ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '0 5px',
  width: '120px',
  zIndex: 3,
  backgroundColor: colorPalette.monotone[0],
})

const TeamSelectBox = styled('div')({
  '& .MuiOutlinedInput-root': {
    backgroundColor: colorPalette.monotone[0],
  },
})

const TermLabel = styled('div')({
  padding: '5px',
})

const SavedSearchConditionButton = styled(Button)({
  cursor: 'pointer',
})

const SavedSearchConditionButtonLabel = styled(Typography)({
  fontSize: '12px',
  whiteSpace: 'nowrap',
})

const StyledSelect = styled(Select)({
  height: '28px',
  fontSize: '11px',
})

const StyledMenuItem = styled(MenuItem)({
  fontSize: '11px',
}) as typeof MenuItem
